/**
 * Reason for locking an action.
 * - 'promise' — auto-locked while the returned Promise is pending
 * - 'manual'  — explicitly locked by consumer (e.g. dialog is open)
 */
export type ActionLockReason = 'promise' | 'manual';

/**
 * Composite key for the action lock Map.
 * Format: `${rowId}:${actionKey}:${reason}`
 *
 * Two entries can coexist for the same (rowId, actionKey) pair with different reasons.
 * The action is considered locked if ANY entry exists for the (rowId, actionKey) pair.
 */
export type ActionLockKey = string;

/**
 * Builds the Map key for a specific (rowId, actionKey, reason) tuple.
 */
export function makeActionLockKey(
  rowId: string,
  actionKey: string,
  reason: ActionLockReason
): ActionLockKey {
  return `${rowId}:${actionKey}:${reason}`;
}

/**
 * Checks whether any lock entry exists for a (rowId, actionKey) pair,
 * regardless of reason.
 */
export function isActionLocked(
  locks: Map<ActionLockKey, number>,
  rowId: string,
  actionKey: string
): boolean {
  return (
    locks.has(makeActionLockKey(rowId, actionKey, 'promise')) ||
    locks.has(makeActionLockKey(rowId, actionKey, 'manual'))
  );
}

/**
 * Returns the set of locked actionKeys for a given rowId.
 * Used by RowActions to determine per-button disabled state.
 */
export function getLockedActionKeysForRow(
  locks: Map<ActionLockKey, number>,
  rowId: string
): Set<string> {
  const result = new Set<string>();
  const prefix = `${rowId}:`;

  for (const key of locks.keys()) {
    if (key.startsWith(prefix)) {
      // key format: "rowId:actionKey:reason" — extract actionKey
      const afterRowId = key.slice(prefix.length);
      const colonIndex = afterRowId.lastIndexOf(':');
      if (colonIndex > 0) {
        result.add(afterRowId.slice(0, colonIndex));
      }
    }
  }

  return result;
}
